import {
  MetricGroup,
  Metric,
  Dimension,
  sortQuery,
  Value,
} from "../../models/Explorer";
import {
  encodeTableState,
  type TableStateSnapshot,
} from "../../util/tableState";
import {
  ShotQueryContext,
  ShotFilters,
  DEFAULT_FILTERS as DEFAULT_SHOT_FILTERS,
} from "../ShotDetailModal/types";
import {
  ActionFilters,
  DEFAULT_ACTION_FILTERS,
} from "../ActionDetailModal/types";
import { currentSeasonOrGuess } from "../../util/season";

const EXPLORER_STARTER_METRICS: Metric[] = [
  { value: "Poss", label: "Poss" },
  { value: "FirstShotPPP", label: "FirstShotPPP" },
  { value: "FirstShotxPPP", label: "FirstShotxPPP" },
  { value: "ePP100", label: "ePP100" },
  { value: "TS%", label: "TS%" },
  { value: "ORB%", label: "ORB%" },
  { value: "TOV%", label: "TOV%" },
  { value: "RimRate", label: "RimRate" },
  { value: "MidRate", label: "MidRate" },
  { value: "3Rate", label: "3Rate" },
  { value: "RimTS%", label: "RimTS%" },
  { value: "MidTS%", label: "MidTS%" },
  { value: "3TS%", label: "3TS%" },
];

/** Filter names that must not appear in the context filters (handled as defaults). */
const DEFAULT_FILTER_NAMES = new Set(["Season", "SeasonType", "Garbage"]);

/**
 * Explorer items defined for ctg only (E3). A query using one refuses to span
 * the 2022-23 → 2023-24 line (`mixed_source_native`), so its seasons are pinned
 * to ctg's.
 */
export const CTG_NATIVE_FILTERS = new Set(["CoverageScrDetail", "DoubleScreenType"]);

/** First season ctg is the default source for (EXPLORER.meta.source_defaults). */
export const CTG_FIRST_SEASON = 2023;

/**
 * The seasons an Explorer link queries. With a ctg-native item a report season
 * on ctg stays as is; no season (or an SS-era one) becomes every ctg season.
 */
export function explorerSeasons(
  season: string | undefined,
  usesCtgNative: boolean,
): string[] {
  if (!usesCtgNative) return season ? [season] : [];
  if (season && Number(season) >= CTG_FIRST_SEASON) return [season];
  const seasons: string[] = [];
  for (let y = CTG_FIRST_SEASON; y <= currentSeasonOrGuess("nba"); y++) {
    seasons.push(String(y));
  }
  return seasons;
}

type ReportType = "season" | "L10" | "L20" | "game";

export interface BuildExplorerQueryOptions {
  reportType?: ReportType;
  /** Additional filters to append (e.g., from active action chart state + breakdown rows). */
  extraFilters?: [string, string, string[]][];
  /** Additional dimensions to add alongside the team dimension. */
  extraDimensions?: string[];
}

/**
 * Build an explorer MetricGroup[] query from a team report row's ShotQueryContext.
 *
 * The resulting query dimensions by TeamOffense/TeamDefense (all teams)
 * with the report row's filters applied, so the user sees league-wide
 * rankings in context.
 *
 * Returns a sortQuery-normalized query so the registered hash matches
 * the hash computed when the explorer auto-executes it.
 */
export function buildExplorerQuery(
  queryContext: ShotQueryContext,
  season: string | undefined,
  options?: BuildExplorerQueryOptions,
): MetricGroup[] {
  const { reportType, extraFilters, extraDimensions } = options ?? {};

  const teamDimName =
    queryContext.perspective === "offense" ? "TeamOffense" : "TeamDefense";

  // Default filters (Season, SeasonType, Garbage) — MUST be first 3 in the array.
  const globalFilters: Dimension[] = [];

  const usesCtgNative =
    (extraFilters ?? []).some(([name]) => CTG_NATIVE_FILTERS.has(name)) ||
    (extraDimensions ?? []).some((name) => CTG_NATIVE_FILTERS.has(name));
  const seasons = explorerSeasons(season, usesCtgNative);
  if (seasons.length) {
    globalFilters.push({
      name: { value: "Season", label: "Season" },
      op: { value: "in", label: "in" },
      values: seasons.map((s) => ({ value: s, label: s })),
    });
  }

  if (reportType !== "game") {
    globalFilters.push({
      name: { value: "SeasonType", label: "SeasonType" },
      op: { value: "in", label: "in" },
      values: [{ value: "regular season", label: "regular season" }],
    });
  }

  globalFilters.push({
    name: { value: "Garbage", label: "Garbage" },
    op: { value: "in", label: "in" },
    values: [{ value: "False", label: "False" }],
  });

  // Map queryContext.filters to Dimension format, excluding default filters
  const contextFilters: Dimension[] = queryContext.filters
    .filter(([name]) => !DEFAULT_FILTER_NAMES.has(name))
    .map(([name, op, values]) => ({
      name: { value: name, label: name },
      op: { value: op, label: op },
      values: values.map((v) => ({ value: v, label: v })),
    }));

  globalFilters.push(...contextFilters);

  // Append extra filters (active action chart state + row-specific overrides)
  if (extraFilters) {
    for (const [name, op, values] of extraFilters) {
      globalFilters.push({
        name: { value: name, label: name },
        op: { value: op, label: op },
        values: values.map((v) => ({ value: v, label: v })),
      });
    }
  }

  // Team as a dimension (not a filter) — shows all teams
  const globalDimensions: Dimension[] = [
    { name: { value: teamDimName, label: teamDimName } },
  ];

  if (extraDimensions) {
    for (const dim of extraDimensions) {
      globalDimensions.push({ name: { value: dim, label: dim } });
    }
  }

  const globalElement: MetricGroup = {
    filters: globalFilters,
    dimensions: globalDimensions,
    ratio: null,
  };

  const metricsElement: MetricGroup = {
    metrics: EXPLORER_STARTER_METRICS,
    filters: [],
    dimensions: [],
    ratio: null,
  };

  return sortQuery([globalElement, metricsElement]);
}

// ============================================================================
// Action detail key → explorer filter name mapping
// ============================================================================

/** Maps action detail filter/breakdown keys to explorer filter names. */
export const ACTION_KEY_TO_FILTER: Record<string, string> = {
  // Common action fields
  actionType: "ActionType",
  actionSimple: "ActionSimple",
  actionSize: "ActionSize",
  direct: "ActionDirect",
  primaryOutcome: "ActionOutcome",
  transition: "Transition",
  chanceType: "ChanceType",
  zone: "Zone",
  primaryPlayer: "PrimaryPlayer",
  secondaryPlayer: "SecondaryPlayer",
  // Pick-specific
  pickType: "PickType",
  bhrDefense: "PickBallSimpleDefense",
  scrDefense: "PickScreenSimpleDefense",
  rollType: "PickRollType",
  pickSide: "PickSide",
  direction: "PickDirection",
  pickOutcome: "PickOutcome",
  coverageReason: "PickCoverageReason",
  pickLocation: "PickLocation",
  pickScreenDirection: "PickScreenDirection",
  pickSpacing: "PickSpacing",
  // ctg-native (CTG_NATIVE_FILTERS): the query they open stays on ctg seasons
  scrDefenseDetail: "CoverageScrDetail",
  doubleScreenType: "DoubleScreenType",
  // Handoff-specific
  handoffType: "HandoffType",
  receiverDefType: "HandoffBallDefense",
  setterDefType: "HandoffScreenDefense",
  handoffSide: "HandoffSide",
  // Isolation-specific
  isoDriveDirection: "IsolationDriveDirection",
  doubled: "IsolationDoubleTeam",
  isoSide: "IsolationSide",
  // Post-specific
  postSection: "PostSection",
  postOutcome: "PostOutcome",
  doubleTeam: "PostDoubleTeam",
  dribbledInto: "PostDribbledInto",
  // Drive-specific
  driveSide: "DriveSide",
  driveDirection: "DriveDirection",
  blowby: "DriveBlowby",
  endType: "DriveEndType",
  driveMiddle: "DriveMiddle",
  driveContext: "DriveContext",
  // Off-ball screen-specific
  screenType: "OffBallScreenType",
  cutterDefType: "OffBallScreenCutterDefense",
  obsSide: "OffBallScreenSide",
  ledToTouch: "OffBallScreenLedToTouch",
  ledToShot: "OffBallScreenLedToShot",
  // Shot-level
  shotType: "ShotType",
  contestLevel: "ContestLevel",
  regionCategory: "SimpleShotRegion",
  catchAndShoot: "CatchAndShoot",
  // No explorer equivalents: rollType, isoSide, dribbledInto
};

// ============================================================================
// Convert active ActionFilters → explorer filter tuples
// ============================================================================

/**
 * Keys where "empty set = show all" (non-empty means actively filtering).
 * The rest use "full set = show all" (reduced set means actively filtering).
 */
const EMPTY_MEANS_ALL_KEYS = new Set([
  "transition",
  "chanceType",
  "zone",
  "actionSimple",
  "primaryPlayer",
  "secondaryPlayer",
  "primaryDefender",
  "secondaryDefender",
]);

/**
 * Convert the active ActionFilters state into explorer filter tuples.
 * Only includes filters that differ from defaults (i.e., the user is actively filtering).
 *
 * Returns a Map keyed by explorer filter name for easy merging with row-specific overrides.
 */
export function actionFiltersToExplorerTuples(
  filters: ActionFilters,
  players: string[] = [],
): Map<string, [string, string, string[]]> {
  const result = new Map<string, [string, string, string[]]>();

  const processKey = (key: string, currentSet: Set<string>) => {
    const explorerName = ACTION_KEY_TO_FILTER[key];
    if (!explorerName) return;

    const isEmptyMeansAll = EMPTY_MEANS_ALL_KEYS.has(key);

    if (isEmptyMeansAll) {
      // Non-empty = actively filtering
      if (currentSet.size > 0) {
        result.set(explorerName, [explorerName, "in", Array.from(currentSet)]);
      }
    } else {
      // Compare to default — if reduced from default, include as filter
      const defaultSet =
        DEFAULT_ACTION_FILTERS[key as keyof ActionFilters] as Set<string>;
      if (defaultSet && currentSet.size < defaultSet.size) {
        result.set(explorerName, [explorerName, "in", Array.from(currentSet)]);
      }
    }
  };

  // Player filters are interned numeric indices; resolve through the players table.
  const resolvePlayerSet = (idxs: Set<number>): Set<string> => {
    const out = new Set<string>();
    for (const i of Array.from(idxs)) {
      const name = players[i];
      if (name) out.add(name);
    }
    return out;
  };

  // Skip actionPresence — no explorer equivalent
  processKey("transition", filters.transition);
  processKey("chanceType", filters.chanceType);
  processKey("zone", filters.zone);
  processKey("actionType", filters.actionType as Set<string>);
  processKey("actionSimple", filters.actionSimple);
  processKey("actionSize", filters.actionSize as Set<string>);
  processKey("direct", filters.direct as Set<string>);
  processKey("primaryOutcome", filters.primaryOutcome as Set<string>);
  processKey("primaryPlayer", resolvePlayerSet(filters.primaryPlayer));
  processKey("secondaryPlayer", resolvePlayerSet(filters.secondaryPlayer));
  processKey("primaryDefender", resolvePlayerSet(filters.primaryDefender));
  processKey("secondaryDefender", resolvePlayerSet(filters.secondaryDefender));

  // Type-specific filters (empty set = all, non-empty = active)
  for (const [field, values] of Object.entries(filters.typeSpecific)) {
    if (values.size > 0) {
      const explorerName = ACTION_KEY_TO_FILTER[field];
      if (explorerName) {
        result.set(explorerName, [explorerName, "in", [...values]]);
      }
    }
  }

  return result;
}

// ============================================================================
// Convert active ShotFilters → explorer filter tuples
// ============================================================================

/** Maps shot filter keys to explorer filter names. */
const SHOT_KEY_TO_FILTER: Record<string, string> = {
  shotType: "SimpleShotType",
  contestLevel: "ContestLevel",
  regionCategory: "SimpleShotRegion",
  catchAndShoot: "CatchAndShoot",
  shooter: "Shooter",
  passer: "Passer",
  contester: "Contester",
  // assisted → "Assisted" but values are "assisted"/"unassisted" vs explorer's true/false
  // fouled → "ShotFouled" but it's a bool filter, values don't map cleanly
};

/** Shot filter keys where "empty set = all" (non-empty = actively filtering). */
const SHOT_EMPTY_MEANS_ALL_KEYS = new Set([
  "shotType",
  "complexShotType",
  "contestLevel",
  "regionCategory",
  "shooter",
  "passer",
  "contester",
]);

/**
 * Convert the active ShotFilters state into explorer filter tuples.
 * Only includes filters that differ from defaults.
 */
export function shotFiltersToExplorerTuples(
  filters: ShotFilters,
): Map<string, [string, string, string[]]> {
  const result = new Map<string, [string, string, string[]]>();

  const processKey = (key: string, currentSet: Set<string>) => {
    const explorerName = SHOT_KEY_TO_FILTER[key];
    if (!explorerName) return;

    if (SHOT_EMPTY_MEANS_ALL_KEYS.has(key)) {
      if (currentSet.size > 0) {
        result.set(explorerName, [explorerName, "in", [...currentSet]]);
      }
    } else {
      // "full set = all" — compare to defaults
      const defaultSet =
        DEFAULT_SHOT_FILTERS[key as keyof ShotFilters] as Set<string>;
      if (defaultSet && currentSet.size < defaultSet.size) {
        result.set(explorerName, [explorerName, "in", [...currentSet]]);
      }
    }
  };

  processKey("shotType", filters.shotType);
  processKey("contestLevel", filters.contestLevel);
  processKey("regionCategory", filters.regionCategory);
  processKey("catchAndShoot", filters.catchAndShoot as Set<string>);
  processKey("shooter", filters.shooter);
  processKey("passer", filters.passer);
  processKey("contester", filters.contester);
  // assisted/fouled skipped — value formats don't match explorer's boolean filter expectations

  return result;
}

// ============================================================================
// Player names → ids for Explorer links
// ============================================================================

/**
 * Explorer filters over player ids whose detail-chart values are names. The
 * Explorer matches ids only, so a link must translate before it registers.
 */
export const PLAYER_NAME_FILTERS = new Set(["Contester"]);

/**
 * Every id the player list gives each name ("Last, First", as the detail
 * payload spells it). Two players sharing a name both match: the link can only
 * over-include, never silently drop the one meant.
 */
export function namesToPlayerIds(names: string[], players: Value[]): string[] {
  const wanted = new Set(names);
  return players.filter((p) => wanted.has(p.label)).map((p) => p.value);
}

/**
 * Swap the names in `PLAYER_NAME_FILTERS` tuples for ids. Throws when a filter
 * resolves to no id, since a query filtered on nothing would read as "no plays".
 */
export function withPlayerIds(
  tuples: Map<string, [string, string, string[]]>,
  players: Value[],
): Map<string, [string, string, string[]]> {
  const out = new Map(tuples);
  for (const [key, [name, op, values]] of Array.from(tuples)) {
    if (!PLAYER_NAME_FILTERS.has(name)) continue;
    const ids = namesToPlayerIds(values, players);
    if (ids.length === 0) {
      throw new Error(`No player id for ${name} ${values.join(", ")}`);
    }
    out.set(key, [name, op, ids]);
  }
  return out;
}

/** The seasons a detail chart covers: its report season, else its context's Season filter. */
export function contextSeasons(
  queryContext: ShotQueryContext,
  season: string | undefined,
): string[] {
  if (season) return [season];
  const seasonFilter = queryContext.filters.find(([name]) => name === "Season");
  return seasonFilter ? seasonFilter[2].map(String) : [];
}

/**
 * Build the final explorer filter tuples by merging active filter state
 * with row-specific overrides. Row values take precedence for the same key.
 */
export function mergeExplorerFilters(
  activeFilters: Map<string, [string, string, string[]]>,
  rowKey?: string,
): [string, string, string[]][] {
  // Start with active filters
  const merged = new Map(activeFilters);

  // Override with row-specific values
  if (rowKey) {
    for (const segment of rowKey.split("|")) {
      const colonIdx = segment.indexOf(":");
      const key = segment.slice(0, colonIdx);
      const value = segment.slice(colonIdx + 1);
      const explorerName = ACTION_KEY_TO_FILTER[key];
      if (explorerName) {
        merged.set(explorerName, [explorerName, "in", [value]]);
      }
    }
  }

  return [...merged.values()];
}

// ============================================================================
// Table state for explorer links (pin team row to top)
// ============================================================================

/**
 * Build a `?ts=` query param value with ranks mode and an optional
 * TeamOffense column filter.
 */
export function buildTeamTableStateParam(teamAbbrev?: string): string {
  const snapshot: TableStateSnapshot = {};
  if (teamAbbrev) {
    snapshot.f = [
      { field: "TeamOffense", operator: "contains", value: teamAbbrev },
    ];
  }
  return encodeTableState(snapshot);
}

/**
 * Build table state for player section explorer links:
 * sort by "% of Poss" desc, filter Poss >= 50, filter TeamOffense to team.
 */
export function buildPlayerSectionTableStateParam(
  teamAbbrev?: string,
): string {
  const snapshot: TableStateSnapshot = {
    s: [{ field: "% of Poss", sort: "desc" }],
    f: [
      { field: "Poss", operator: ">=", value: "50" },
      ...(teamAbbrev
        ? [{ field: "TeamOffense", operator: "contains", value: teamAbbrev }]
        : []),
    ],
  };
  return encodeTableState(snapshot);
}

// ============================================================================
// Player section explorer queries (closeouts, actions)
// ============================================================================

export interface PlayerSectionQueryOptions {
  season?: string;
  /** Secondary dimension alongside OffensiveChancePlayer */
  secondaryDimension: string;
  /** The "as" filter target (e.g. "CloseoutBallhandler" or "PrimaryPlayer") */
  dependentFilter: string;
}

/**
 * Build an explorer query for player section tables (closeouts, actions).
 *
 * Dimensions: TeamOffense, OffensiveChancePlayer, {secondaryDimension}
 * Filters: Season, SeasonType, Garbage, OffensiveChancePlayer as CloseoutBallhandler
 */
export function buildPlayerSectionQuery(
  options: PlayerSectionQueryOptions,
): MetricGroup[] {
  const { season, secondaryDimension, dependentFilter } = options;

  const globalFilters: Dimension[] = [];

  if (season) {
    globalFilters.push({
      name: { value: "Season", label: "Season" },
      op: { value: "in", label: "in" },
      values: [{ value: season, label: season }],
    });
  }

  globalFilters.push({
    name: { value: "SeasonType", label: "SeasonType" },
    op: { value: "in", label: "in" },
    values: [{ value: "regular season", label: "regular season" }],
  });

  globalFilters.push({
    name: { value: "Garbage", label: "Garbage" },
    op: { value: "in", label: "in" },
    values: [{ value: "False", label: "False" }],
  });

  // OffensiveChancePlayer as {dependentFilter}
  globalFilters.push({
    name: { value: "OffensiveChancePlayer", label: "OffensiveChancePlayer" },
    op: { value: "as", label: "as" },
    values: [{ value: dependentFilter, label: dependentFilter }],
    params: 1,
  });

  const globalDimensions: Dimension[] = [
    { name: { value: "TeamOffense", label: "TeamOffense" }, primary: true },
    { name: { value: "OffensiveChancePlayer", label: "OffensiveChancePlayer" }, primary: true },
    { name: { value: secondaryDimension, label: secondaryDimension } },
  ];

  const globalElement: MetricGroup = {
    filters: globalFilters,
    dimensions: globalDimensions,
    ratio: null,
    dimensionRatio: { value: "Poss", label: "Poss" },
    includeOverall: true,
  };

  const metricsElement: MetricGroup = {
    metrics: EXPLORER_STARTER_METRICS,
    filters: [],
    dimensions: [],
    ratio: null,
  };

  return sortQuery([globalElement, metricsElement]);
}
